import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { notificationApi, pricingRulesApi, kanbanApi, usersApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SETTINGS } from '@/constants/testIds';
import { Badge } from '@/components/ui/badge';

export default function SettingsPage() {
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [newStageName, setNewStageName] = useState('');

  const load = async () => {
    const [t, r, s, u] = await Promise.all([
      notificationApi.listTemplates(), pricingRulesApi.list(), kanbanApi.list(), usersApi.list(),
    ]);
    setTemplates(t); setRules(r); setStages(s); setUsers(u);
  };
  useEffect(() => { load(); }, []);

  const saveTemplate = async (t) => {
    try {
      await notificationApi.updateTemplate(t.id, {
        trigger: t.trigger,
        channels: t.channels || [],
        sms_body: t.sms_body || '',
        email_subject: t.email_subject || '',
        email_body: t.email_body || '',
        enabled: !!t.enabled,
      });
      toast.success('Template saved');
      load();
    } catch { toast.error('Failed'); }
  };

  const invite = async (e) => {
    e.preventDefault();
    try {
      await usersApi.inviteCleaner({ ...inviteForm, role: 'cleaner' });
      toast.success('Cleaner invited (password saved)');
      setInviteOpen(false);
      setInviteForm({ name: '', email: '', phone: '', password: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const addStage = async () => {
    if (!newStageName.trim()) return;
    await kanbanApi.create({ name: newStageName, order: stages.length });
    setNewStageName(''); load();
  };

  const delStage = async (id) => { await kanbanApi.delete(id); load(); };
  const delRule = async (id) => { await pricingRulesApi.delete(id); load(); };

  return (
    <AppShell title="Settings">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Configure notifications, pricing, workflows and team.</p>
      </div>

      <Tabs defaultValue="notifications">
        <TabsList>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="pricing">Pricing Rules</TabsTrigger>
          <TabsTrigger value="kanban">Kanban Stages</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="mt-4">
          <div className="grid grid-cols-1 gap-3">
            {templates.map(t => (
              <Card key={t.id}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">{t.trigger}</CardTitle>
                  <div className="flex items-center gap-3"><Switch checked={!!t.enabled} onCheckedChange={v => setTemplates(templates.map(x => x.id === t.id ? { ...x, enabled: v } : x))} /><span className="text-xs">{t.enabled ? 'Enabled' : 'Disabled'}</span></div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex gap-2">
                    {['sms', 'email'].map(ch => (
                      <label key={ch} className="flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={(t.channels || []).includes(ch)} onChange={e => {
                          const c = new Set(t.channels || []);
                          if (e.target.checked) c.add(ch); else c.delete(ch);
                          setTemplates(templates.map(x => x.id === t.id ? { ...x, channels: Array.from(c) } : x));
                        }} />
                        {ch.toUpperCase()}
                      </label>
                    ))}
                  </div>
                  {(t.channels || []).includes('sms') && <div><Label className="text-xs">SMS body</Label><Textarea rows={2} value={t.sms_body || ''} onChange={e => setTemplates(templates.map(x => x.id === t.id ? { ...x, sms_body: e.target.value } : x))} /></div>}
                  {(t.channels || []).includes('email') && (<><div><Label className="text-xs">Email subject</Label><Input value={t.email_subject || ''} onChange={e => setTemplates(templates.map(x => x.id === t.id ? { ...x, email_subject: e.target.value } : x))} /></div><div><Label className="text-xs">Email body</Label><Textarea rows={3} value={t.email_body || ''} onChange={e => setTemplates(templates.map(x => x.id === t.id ? { ...x, email_body: e.target.value } : x))} /></div></>)}
                  <div className="flex justify-end"><Button size="sm" onClick={() => saveTemplate(t)} data-testid={SETTINGS.saveTemplate}><Save className="h-3 w-3 mr-1" />Save</Button></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="mt-4">
          <div className="space-y-3">
            {rules.map(r => (
              <Card key={r.id}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">{r.name} {r.is_default && <Badge>Default</Badge>}</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => delRule(r.id)}><Trash2 className="h-4 w-4" /></Button>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div>Base: <span className="font-semibold">${r.base}</span></div>
                  <div>Per bedroom: ${r.per_bedroom}</div>
                  <div>Per bathroom: ${r.per_bathroom}</div>
                  <div>Per sqft: ${r.per_sqft}</div>
                  <div>Hourly: ${r.hourly_rate}</div>
                  <div>Min price: ${r.min_price}</div>
                  <div>Tax: {(r.tax_rate * 100).toFixed(1)}%</div>
                  <div>Extras: {(r.extras || []).length}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Input value={newStageName} onChange={e => setNewStageName(e.target.value)} placeholder="New stage name" />
            <Button onClick={addStage} data-testid={SETTINGS.addKanbanStage}><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
          <Card><CardContent className="p-0"><div className="divide-y">
            {stages.map(s => (
              <div key={s.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                <div><div className="font-medium text-sm">{s.name}</div><div className="text-xs text-muted-foreground">Order {s.order}</div></div>
                <Button variant="ghost" size="icon" onClick={() => delStage(s.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div></CardContent></Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">{users.length} team member(s)</div>
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild><Button data-testid={SETTINGS.inviteCleaner}><Plus className="h-4 w-4 mr-2" />Invite cleaner</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Invite a cleaner</DialogTitle></DialogHeader>
                <form onSubmit={invite} className="space-y-3">
                  <div><Label>Name *</Label><Input required value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} /></div>
                  <div><Label>Email *</Label><Input required type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} /></div>
                  <div><Label>Password *</Label><Input required type="password" value={inviteForm.password} onChange={e => setInviteForm({ ...inviteForm, password: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={inviteForm.phone} onChange={e => setInviteForm({ ...inviteForm, phone: e.target.value })} /></div>
                  <DialogFooter><Button type="submit">Invite</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <Card><CardContent className="p-0"><div className="divide-y">
            {users.map(u => (
              <div key={u.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                <div><div className="font-medium text-sm">{u.name}</div><div className="text-xs text-muted-foreground">{u.email} • {u.phone || '—'}</div></div>
                <Badge>{u.role}</Badge>
              </div>
            ))}
          </div></CardContent></Card>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
